// ============================================================================
// Señal en vivo de accesos (RTDB `yardAccessLive/{yardId}`)
// ============================================================================
// El backend escribe `yardAccessLive/{yardId} = { ts, device_id, created }`
// cada vez que ingiere un evento de acceso. La señal NO trae el evento: sólo
// avisa "hay algo nuevo en este patio", y la página responde corriendo el
// mismo tick que el poll (`refreshEvents` + `loadInspections`). Así el dato
// sigue saliendo siempre del endpoint, con sus filtros y su recorte por patio
// asignado, y la RTDB no se vuelve una segunda fuente de verdad.
//
// Este módulo es puro a propósito: en un worktree sin `apps/v5/.env` todo lo
// que importa `firebaseConfig` revienta al colectar (auth/invalid-api-key),
// así que la página no es testeable; las decisiones sí, acá.
// ============================================================================

/**
 * Techo de listeners simultáneos. Un supervisor sin patio elegido ve todos los
 * de su alcance; cada uno es un `onValue` sobre un nodo de ~3 campos, barato,
 * pero no queremos abrir cientos si algún día el alcance "all" crece. Pasado el
 * techo, los patios que quedan afuera se siguen cubriendo con el poll de 5 s
 * (ver `isLiveSignalHealthy`: con patios sin listener la señal no cuenta como
 * sana, así que el poll no se espacia).
 */
export const LIVE_SIGNAL_MAX_YARDS = 25;

/** El poll de siempre: es lo que corre si la señal no está o falla. */
export const POLL_FALLBACK_MS = 5000;

/**
 * Con la señal sana el poll no se apaga: se espacia. Sigue cubriendo lo que la
 * señal no ve — un `set` del backend que falló, un evento escrito por otra vía
 * que no toca la RTDB, una desconexión que `.info/connected` tarda en notar —
 * con una latencia peor, pero acotada a 30 s en vez de "nunca".
 */
export const POLL_WITH_LIVE_SIGNAL_MS = 30000;

/**
 * Ventana para juntar señales en un solo refresh. El backend escribe la señal
 * por CADA evento ingerido, y un sync que trae varios eventos atrasados los
 * escribe uno detrás de otro; sin la ventana serían N fetches de la misma
 * lista (no medimos todavía cuántas escrituras llegan por ráfaga). La ventana
 * arranca con la PRIMERA señal y no se reinicia, así que la latencia agregada
 * está acotada a 500 ms aunque la ráfaga siga.
 */
export const LIVE_SIGNAL_COALESCE_MS = 500;

// Caracteres que la RTDB no admite en una clave. Los ids de patio son auto-ids
// de Firestore y no los traen, pero un id raro armaría una ruta a otro nodo
// (`/`) o haría tirar a `ref()`; mejor no suscribirse que romper la página.
const INVALID_KEY = /[.#$[\]/]/;

/**
 * Qué patios escuchar: los elegidos si hay selección; si no, los que el
 * usuario puede ver (las mismas opciones del combo). Nada si la pantalla tiene
 * las cargas apagadas (`skipYardQueries`): no hay lista que refrescar.
 */
export function liveSignalYardIds(input: {
  skip: boolean;
  selectedYardIds: readonly string[];
  optionYardIds: readonly string[];
}): { yardIds: string[]; complete: boolean } {
  if (input.skip) return { yardIds: [], complete: false };
  const source = input.selectedYardIds.length > 0 ? input.selectedYardIds : input.optionYardIds;
  const out: string[] = [];
  const seen = new Set<string>();
  // `complete` = todos los patios del alcance tienen listener. Si alguno quedó
  // afuera (techo o id inválido) su pase sólo llega por el poll, y la página no
  // puede espaciarlo a 30 s sin dejar a ese patio con 30 s de latencia.
  let complete = true;
  for (const id of source) {
    if (!id || seen.has(id)) continue;
    seen.add(id);
    if (INVALID_KEY.test(id) || out.length >= LIVE_SIGNAL_MAX_YARDS) {
      complete = false;
      continue;
    }
    out.push(id);
  }
  return { yardIds: out, complete };
}

/** `ts` de la señal, o 0 si el nodo no existe o no tiene la forma esperada. */
export function readSignalTs(value: unknown): number {
  if (!value || typeof value !== "object") return 0;
  const ts = (value as { ts?: unknown }).ts;
  return typeof ts === "number" && Number.isFinite(ts) ? ts : 0;
}

/**
 * Decide si un snapshot de `yardAccessLive/{yardId}` tiene que refrescar.
 *
 * `lastTs === undefined` es el PRIMER snapshot del listener: `onValue` entrega
 * el valor actual al suscribirse, y ese valor describe un pase que la carga
 * inicial ya trajo. Refrescar ahí duplicaría el fetch de montaje (y cada cambio
 * de filtro, que re-suscribe). Sólo fija la marca. Si el nodo todavía no
 * existe la marca queda en 0, para que la primera escritura real sí dispare.
 *
 * Después, sólo un `ts` estrictamente mayor dispara. Igual o menor no: el SDK
 * re-entrega el valor cacheado al reconectar, y una reescritura con un `ts`
 * viejo no es un pase nuevo. La marca nunca retrocede.
 */
export function shouldTriggerRefresh(
  lastTs: number | undefined,
  value: unknown,
): { trigger: boolean; lastTs: number } {
  const ts = readSignalTs(value);
  if (lastTs === undefined) return { trigger: false, lastTs: ts };
  if (ts > lastTs) return { trigger: true, lastTs: ts };
  return { trigger: false, lastTs };
}

/**
 * La señal sólo cuenta como sana si la RTDB está conectada, CADA patio
 * suscrito ya entregó su primer snapshot (prueba de que las reglas permiten
 * leerlo) y ninguno dio error. Las reglas de `yardAccessLive` viven en la
 * consola de Firebase, no en el repo, y no se pudieron verificar: un
 * `permission_denied` en un solo patio deja ese patio sin aviso, así que el
 * poll tiene que seguir a 5 s por él.
 */
export function isLiveSignalHealthy(state: {
  /** `liveSignalYardIds(...).complete`; omitido cuenta como completo. */
  complete?: boolean;
  connected: boolean;
  subscribed: number;
  ready: number;
  errored: number;
}): boolean {
  return (
    state.complete !== false &&
    state.connected &&
    state.subscribed > 0 &&
    state.errored === 0 &&
    state.ready >= state.subscribed
  );
}

export function pollIntervalMs(liveSignalHealthy: boolean): number {
  return liveSignalHealthy ? POLL_WITH_LIVE_SIGNAL_MS : POLL_FALLBACK_MS;
}
